import { APP_BASE_HREF } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';


import { Debounce } from 'lodash-decorators';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';


import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { ActionKeys } from '../../../Common/Forms/enums/keys.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ConfigService } from '../../../Core/Config/services/config.service';

import { PartnerResellers } from '../../../Core/Config/enums/partner-resellers.enum';

import { partnerResellerConfigs, ResellerConfig } from '../../config/resellers/reseller-config-model';
import { LoginModel } from '../../models/login-info.model';
import { AuthenticationError } from '../../services/error-handler.service';
import { LoginService, LoginType } from '../../services/login.service';
import { McpOauthService } from '../../services/mcp-oauth.service';
import { WebsocketAwareNeptuneService } from '../../services/websocket-aware-neptune.service';


export enum LoginErrorTypes {
    sessionLimit = 'SESSION_LIMIT_EXCEEDED',
}

enum LoginMessageState {
    none,
    loginInProgress,
    microsoftConsentFailure,
    authError,
    authErrorLockOut,
    redirectFromPasswordLogin,
}

@Component({
    selector: 'agpr-sso-login',
    templateUrl: './sso-login.component.html',
    styleUrls: ['./sso-login.component.scss'],
    standalone: false
})
export class SsoLoginComponent implements OnDestroy {
    static readonly SSO_LOGIN_REDIRECT_PARAM = 'ssoRedirect';

    loading: boolean = false;

    logoUrl: string;
    smallLogoUrl: string;
    returnUrl: string;

    loginForm: FormGroup;
    partnerReseller: PartnerResellers;
    isResellerDomain: boolean = false;
    resellerConfig: ResellerConfig;
    loginMessageState: LoginMessageState = LoginMessageState.none;

    logoAltText = 'Agility PR Solutions';

    readonly loginMessageStateEnum = LoginMessageState;
    readonly partnerResellerEnum = PartnerResellers;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly id = 'ssoLogin';

    private subscriptions: Subscription[] = [];
    private loginState$ = this.activeRoute.queryParams.pipe(
        map(params => {
            if (params[SsoLoginComponent.SSO_LOGIN_REDIRECT_PARAM]) {
                this.loginMessageState = LoginMessageState.redirectFromPasswordLogin;
            }
            return this.loginMessageState;
        })
    );
    private ssoLogin$ = this.activeRoute.queryParams.pipe(
        filter(params => params && (this.hasCodeAndState(params) || this.hasMSError(params))),
        mergeMap(params => {
            if (this.hasCodeAndState(params)) {
                const username = this.getUsernameFromState(params['state']);
                this.username.setValue(username);
                this.loginMessageState = LoginMessageState.loginInProgress;
                return this.loginService.performSsoCodeExchange(params['code'], params['state']).pipe(
                    tap(() => this.loginMessageState = LoginMessageState.none)
                );
            }

            this.loginMessageState = LoginMessageState.microsoftConsentFailure;
            return EMPTY;
        }),
        catchError((err: HttpErrorResponse | AuthenticationError) => {
            if ((err as HttpErrorResponse)?.status === 429) {
                // Rate limit for failed login attempts
                this.loginMessageState = LoginMessageState.authErrorLockOut;
            } else {
                this.loginMessageState = LoginMessageState.authError;
            }

            throw err;
        }),
    );

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private loginService: LoginService,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private configService: ConfigService,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private mcpOauthService: McpOauthService,
    ) {
        this.logoUrl = appBaseHref + 'assets/images/agility-logo-vertical.png';
        this.logoAltText = 'Agility PR Solutions';

        // Preserve any MCP / Connector OAuth context across the SSO round-trip so it can
        // be replayed once the code exchange completes (see LoginService.postLogin).
        this.mcpOauthService.captureContextFromUrl();

        this.loginForm = new FormGroup({
            username: new FormControl<string>(null, Validators.required),
        });

        this.configService.setBrandGUID();
        if (this.configService.hasBrandGUID() && !this.configService.getNoRedirect()) {
            this.isResellerDomain = true;
            this.partnerReseller = this.configService.getPartnerReseller();
            this.resellerConfig = partnerResellerConfigs.get(this.partnerReseller);
            if (!!this.resellerConfig.logoUrl) {
                this.logoUrl = appBaseHref + this.resellerConfig.logoUrl;
            }
            if (!!this.resellerConfig.logoAltText) {
                this.logoAltText = this.resellerConfig.logoAltText;
            }
        }
        this.subscriptions.push(
            this.loginState$.subscribe(),
            this.ssoLogin$.subscribe()
        );
    }


    get username(): FormControl<string> {
        return this.loginForm.get('username') as FormControl;
    }


    ssoSignIn() {
        const username = this.username.value?.trim();
        if (!username) {
            return;
        }

        this.loading = true;
        const loginModel: LoginModel = {
            loginInfo: {
                username: username,
                password: null
            },
            forceLogin: false,
            returnUrl: ''
        };
        this.loginService.performSsoLogin(loginModel).subscribe({
            next: (loginResult) => {
                this.loading = false;
                if (loginResult.loginType === LoginType.SSO && loginResult.ssoResponse?.redirectUri) {
                    window.location.href = loginResult.ssoResponse.redirectUri;
                    return;
                }
            },
            error: () => {
                this.loading = false;
                this.router.navigate(['/login'], { state: { username: username }});
            }
        });
    }

    @Debounce(1000, {leading: false})
    onEnter(event) {
        if (event.key === ActionKeys.ENTER) {
            this.ssoSignIn();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private getUsernameFromState(stateParam: string): string {
        const decodedState = atob(stateParam);
        // username:nonce
        const [username] = decodedState.split(':');
        return username;
    }

    private hasCodeAndState(params: Params): boolean {
        return params['code'] && params['state'];
    }

    private hasMSError(params: Params): boolean {
        return params['error'] === 'access_denied' && params['error_subcode'] === 'cancel';
    }
}
